<template>
  <VcLayout v-if="userHasRelevantFF" column
            class="layout-menu-container py-4"
            :class="{
              'parent-directory-container': hasParentDirectory,
              'layout-menu-container--floating-bottom-bar': hasFloatingBottomBar,
            }">
    <template v-if="showMenuTitleHeader">
      <div
        v-if="showHomeButton"
        class="main-menu__home-menu-group mx-4"
        data-qa="main-menu-home-button-group"
      >
        <VcMenuItem
          :label="homeButtonLabel"
          :submenu-items="[]"
          data-qa="main-menu-home-button"
          @click="$emit('homeClick')"
        >
          <div class="main-menu__home-menu-item-content">
            <span class="main-menu__home-menu-item-logo">
              <VcImage
                v-if="homeButtonLogo"
                :image="homeButtonLogo"
                data-qa="main-menu-home-button-logo"
              />
              <VcIcon v-else aria-hidden="true" size="20">icon-Home_POV</VcIcon>
            </span>
            <span class="main-menu__home-menu-item-label">
              <span class="main-menu__home-menu-item-label-primary">{{ homeButtonPrimaryText }}</span>
              <span v-if="homeButtonSecondaryText"> {{ homeButtonSecondaryText }}</span>
            </span>
          </div>
        </VcMenuItem>
      </div>
      <div
        class="main-menu__title-header mx-4"
        data-qa="main-menu-title"
      >
        {{ menuTitle }}
      </div>
    </template>
    <VcProfileIndication
      v-else
      class="profile-indication mx-4"
      :title="staffData.display_name"
      :subTitle="staffData.email"
      :avatar="profileAvatar"
      :showArrow="false">
    </VcProfileIndication>
    <VcMenuItemsGroup
      class="menu-items-group mx-4"
      :class="{
        'menu-items-group--with-profile-header': !showMenuTitleHeader,
        'menu-items-group--with-title-header': showMenuTitleHeader,
        hasParentDirectory: 'parent-directory-menu-items-group',
      }"
      :groupedMenuItems="mappedMenuItems"
      :isMenuLoading="isMenuLoading"
      @menuItemClick="onMenuItemClick"
      @subMenuItemClick="onMenuItemClick"
      @rightAreaClick="onRightAreaClick">
      <template #right-area-checklist>
        <VcProgressCircular
          v-if="checklistProgress.show"
          :currentStep="checklistProgress.currentStep"
          :steps="checklistProgress.totalSteps"
        />
      </template>
      <template #right-area-inbox>
        <VcBadge v-if="conversationCounts" :badgeText="conversationCounts" inline flavor="neutral"></VcBadge>
      </template>
    </VcMenuItemsGroup>
    <PoweredByContainer :text-color="textColor"/>
    <div
      v-if="hasFloatingBottomBar"
      class="layout-menu-container__bottom-spacer"
      aria-hidden="true"
    />
  </VcLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import PoweredByContainer from '@/modules/layout/mainMenu/components/PoweredByContainer.vue';

export default {
  name: 'MainMenu',
  components: { PoweredByContainer },
  props: {
    dataQa: {
      type: String,
      default: 'MainMenu',
    },
    userHasRelevantFF: {
      type: Boolean,
      default: false,
    },
    staffData: {
      type: Object,
      default: () => ({}),
    },
    profileAvatar: {
      type: Object,
      default: () => ({
        colorId: 27,
        name: '',
        imagePath: '',
      }),
    },
    mappedMenuItems: {
      type: Array,
      default: () => [],
    },
    isMenuLoading: {
      type: Boolean,
      default: true,
    },
    checklistProgress: {
      type: Object,
      default: () => ({
        show: false,
        currentStep: 0,
        totalSteps: 0,
      }),
    },
    conversationCounts: {
      type: Number,
      default: 0,
    },
    hasFloatingBottomBar: {
      type: Boolean,
      default: false,
    },
    showMenuTitleHeader: {
      type: Boolean,
      default: false,
    },
    menuTitle: {
      type: String,
      default: '',
    },
    showHomeButton: {
      type: Boolean,
      default: false,
    },
    homeButtonLabel: {
      type: String,
      default: '',
    },
    homeButtonLogo: {
      type: [String, Object],
      default: null,
    },
  },
  created() {
    this.setTopBarConfig(this.menuTopBarConfig);
  },
  computed: {
    ...mapGetters('BusinessStore', ['directorySettings', 'textColor']),
    menuTopBarConfig() {
      const firstButton = {
        identifier: 1,
        icon: 'icon-Magnifi-Glass2',
        actionEvent: 'MainMenuStore/openSearchAction',
        dispatchActionType: 'pov',
      };
      const topBarConfig = {
        title: this.menuTitle || this.$t('layout.menu.topbar_title'),
        firstButton,
        show: true,
      };
      return topBarConfig;
    },
    hasParentDirectory() {
      return this.directorySettings.has_parent_directory;
    },
    homeButtonPrimaryText() {
      return this.homeButtonLabel.replace(/\sHome$/i, '');
    },
    homeButtonSecondaryText() {
      return /\sHome$/i.test(this.homeButtonLabel) ? 'Home' : '';
    },
  },
  methods: {
    ...mapActions('LayoutTopBarStore', ['setTopBarConfig']),
    onMenuItemClick(event) {
      this.$emit('menuItemClick', event);
    },
    onRightAreaClick(event) {
      this.$emit('rightAreaClick', event);
    },
  },
};
</script>

<style lang="scss" scoped>
$bottom_bar_height: 48px;

.main-menu__title-header {
  align-self: stretch;
  text-align: start;
  font-weight: var(--font-weight-large);
  font-size: var(--font-size-medium);
  line-height: var(--size-value6);
  color: var(--gray-darken-5);
}

.main-menu__home-menu-group {
  flex: 0 0 auto;
  margin-bottom: var(--size-value4);
  border-radius: 10px;
  box-shadow: var(--shadow-1);
  overflow: hidden;
  background-color: white;
}

.main-menu__home-menu-item-content {
  display: flex;
  align-items: center;
  gap: var(--size-value5);
  min-width: 0;
}

.main-menu__home-menu-item-logo {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  width: var(--size-value8);
  height: var(--size-value8);
  flex: 0 0 var(--size-value8);

  &::v-deep {
    .VcImage {
      width: 100%;
      height: 100%;
      justify-content: start;
    }

    .image {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
}

.main-menu__home-menu-item-label {
  min-width: 0;
  color: var(--gray-darken-5);
  font-weight: var(--font-weight-medium);
}

.main-menu__home-menu-item-label-primary {
  font-weight: 700;
}

.layout-menu-container{
  max-height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  background-color: rgba(242, 243, 245, 1);
  position: absolute;
  top: var(--vc-top-bar-height);
  bottom: $bottom_bar_height;
  right:0;
  left:0;

  &--floating-bottom-bar {
    bottom: 0;
    scroll-padding-bottom: var(--floating-mobile-bottom-bar-offset, 88px);
  }

  &__bottom-spacer {
    flex: 0 0 var(--floating-mobile-menu-spacer, 64px);
  }

  .menu-items-group{
    &--with-profile-header {
      margin-top: var(--size-value4);
    }

    &--with-title-header {
      margin-top: var(--size-value2);
    }

    &::v-deep{
      .VcMenuItemsGroup {
        background-color: white;
      }
    }
  }
}

.parent-directory-container {
  background-color: #ffffff;

  .parent-directory-menu-items-group{
    &::v-deep{
      .VcMenuItemsGroup {
        box-shadow: none;
        border: var(--gray-lighten-2) 1px solid;
      }
    }
  }
}

</style>
